import { SaveToBoard } from "@faire/retailer-visitor-shared/features/RetailerBoards/Components/SaveToBoard";
import { SaveToBoardProvider } from "@faire/retailer-visitor-shared/features/RetailerBoards/Context";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useMultiUserGTMV2NoConstraints } from "@faire/retailer-visitor-shared/hooks/useMultiUserGTMV2";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getBestProductImage } from "@faire/retailer-visitor-shared/lib/product";
import { useSettingPDPUXPhase2 } from "@faire/retailer-visitor-shared/settings/useSettingPDPUXPhase2";
import { PageName } from "@faire/retailer-visitor-shared/views/multi-user/Share/consts";
import { ShareButton } from "@faire/retailer-visitor-shared/views/multi-user/Share/ShareButton";
import { getProductHeaderName } from "@faire/retailer-visitor-shared/views/product/__internal__/SEOMetaText";
import { IProductDetailsHeaderProps } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Header";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useTraceComponentRender } from "@faire/web--source/common/tracing/useTraceComponentRender";
import { Row, Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import {
  Typography,
  TypographyStyles,
} from "@faire/web--source/slate/Typography";
import { ShareableItemType } from "@faire/web-api--source/indigofair/retailer/ShareableItemType";
import React from "react";
import styled from "styled-components";

export const ProductName: React.FC<
  Pick<
    IProductDetailsHeaderProps,
    "brand" | "isQuickAddModal" | "centerTitle" | "product"
  >
> = ({ brand, isQuickAddModal, centerTitle, product }) => {
  useTraceComponentRender("ProductName");
  const { strictLocalize } = useStrictLocalization();
  const showPDPUXPhase2 = useSettingPDPUXPhase2();
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();
  const getSettingMultiUserGTMV2NoConstraints =
    useMultiUserGTMV2NoConstraints();
  const productHeaderName = getProductHeaderName(
    {
      productName: product.name,
      isFaireDirectActiveInLoggedOut,
    },
    strictLocalize
  );

  return (
    <Row style={{ width: "100%" }} justify={centerTitle ? "center" : undefined}>
      <Column>
        {showPDPUXPhase2 ? (
          <Typography
            as="h1"
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              xLargeAndAbove: "displaySSerifRegular",
            }}
          >
            {productHeaderName}
          </Typography>
        ) : (
          <ProductNameAndPrice
            $isQuickAddModal={isQuickAddModal}
            $centerTitle={centerTitle}
            data-test-id="product-name"
          >
            {productHeaderName}
          </ProductNameAndPrice>
        )}
      </Column>
      {brand?.token && isLoggedInRetailer() ? (
        <Row align="baseline" style={{ marginLeft: "auto" }}>
          <SaveToBoardProvider value={{ trackOrigin: "pdp" }}>
            <SaveToBoard
              iconLightVersion
              iconSize="22px"
              productToken={product.token!}
              productImage={getBestProductImage(product)}
              boardTokens={product.board_tokens}
              brandToken={brand.token}
            />
          </SaveToBoardProvider>
          {!isQuickAddModal ? (
            <>
              <Spacer size="1x" />
              <ShareButton
                type={ShareableItemType.PRODUCT}
                image={product.images?.[0]}
                name={product.name}
                subText={brand.name}
                token={product.token}
                variant="tertiary"
                pageName={
                  getSettingMultiUserGTMV2NoConstraints
                    ? PageName.GTM_PDP
                    : PageName.PDP
                }
              />
            </>
          ) : null}
        </Row>
      ) : null}
    </Row>
  );
};

// For SEO purpose, we have to make this h1 tag, please don't change unless
// consulted with the SEO team
const ProductNameAndPrice = styled.h1<{
  $isQuickAddModal?: boolean;
  $centerTitle?: boolean;
}>`
  ${(props) =>
    TypographyStyles({
      variant: "pageHeaderSerifRegular",
      color: "black",
      theme: props.theme,
    })};

  position: relative;
  word-break: break-word;
  text-align: ${(props) => (props.$centerTitle ? "center" : "left")};
  /* Leave some space for hovering heart */
  padding-right: ${(props) => (props.$isQuickAddModal ? "0" : "30px")};
`;
